import React, { useState } from "react";
import {
  Calendar,
  Building2,
  User,
  Filter as FilterIcon,
  X,
  Loader2,
} from "lucide-react";
import DatePicker from "components/financial/DatePicker";
import SearchSelect from "components/ui/SearchSelect";

/* -------------------------------------------------------------------------- */
/*  Filter — filters card for the financial dashboard                         */
/*                                                                            */
/*  Props:                                                                    */
/*    filters       { period, startDate, endDate, companyId, userId }         */
/*    companiesData array of companies                                        */
/*    usersData     array of users                                            */
/*    onFilterChange(key, value)                                              */
/*    onDateChange({ startDate, endDate })                                    */
/*    onClear()                                                               */
/* -------------------------------------------------------------------------- */

const Filter = ({
  filters,
  companiesData,
  usersData,
  onFilterChange,
  onDateChange,
  onClear,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // SearchSelect works with { value, label } option objects, so we build the
  // option lists here and map back to the plain id stored in `filters`.
  const companyOptions = [
    { value: "", label: "All Companies" },
    ...(companiesData || []).map((c) => ({ value: c._id, label: c.name })),
  ];

  const userOptions = [
    { value: "", label: "All Users" },
    ...(usersData || []).map((u) => ({ value: u._id, label: u.name })),
  ];

  const selectedCompany =
    companyOptions.find((o) => o.value === filters.companyId) || null;
  const selectedUser =
    userOptions.find((o) => o.value === filters.userId) || null;

  return (
    <div className="bg-white rounded-lg border border-mediumgray p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FilterIcon className="w-4 h-4 text-darkgray" />
          <p className="font-semibold text-black">Filters</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="text-sm text-darkgray hover:text-black md:hidden"
        >
          {showFilters ? "Hide" : "Show"}
        </button>
      </div>

      <div
        className={`space-y-4 ${showFilters ? "block mt-4" : "hidden"} md:block md:mt-4`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Period */}
          <div>
            <label className="form-label">Period</label>
            <DatePicker
              value={{
                startDate: filters.startDate,
                endDate: filters.endDate,
              }}
              onChange={onDateChange}
            />
          </div>

          {/* Company */}
          <div>
            <label className="form-label">Company</label>
            <SearchSelect
              options={companyOptions}
              value={selectedCompany}
              onChange={(option) =>
                onFilterChange("companyId", option ? option.value : "")
              }
              placeholder="All Companies"
            />
          </div>

          {/* User */}
          <div>
            <label className="form-label">User</label>
            <SearchSelect
              options={userOptions}
              value={selectedUser}
              onChange={(option) =>
                onFilterChange("userId", option ? option.value : "")
              }
              placeholder="All Users"
              disabled={!filters.companyId && !usersData?.length}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={onClear} className="outline-button">
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default Filter;
